/*
 * LWB Blog Article
 * Highlights the current section in the "On this page" panel, smooth-scrolls the
 * jump links, and keeps wide tables from pushing the page sideways.
 *
 * Heading ids and the table of contents are rendered server side by
 * sections/lwb-article.liquid, so nothing here is required for the page to work.
 */
(function () {
  'use strict';

  function init(root) {
    var body = root.querySelector('[data-lwb-body]');
    if (!body) return;

    wrapTables(body);
    setupScrollSpy(root, body);
    setupStickySidebar(root);
  }

  /*
   * Sticky sidebar.
   *
   * `position: sticky; top: <offset>` only works while the element is shorter
   * than the viewport. A taller sidebar would pin immediately and its bottom
   * would never be reachable, which is why it previously needed its own
   * scrollbar.
   *
   * Instead, when the sidebar is taller than the viewport we set a NEGATIVE
   * top: the sidebar then scrolls up with the page until its bottom edge meets
   * the bottom of the screen, and pins there. No inner scrollbar, and the whole
   * sidebar stays reachable.
   */
  function setupStickySidebar(root) {
    var inner = root.querySelector('.lwb-art__side-inner');
    if (!inner) return;

    var BOTTOM_GAP = 24;
    var DESKTOP = 990;   // must match the CSS breakpoint
    var frame = null;

    function offset() {
      var v = parseInt(getComputedStyle(root).getPropertyValue('--lwb-sticky-offset'), 10);
      return isNaN(v) ? 100 : v;
    }

    function reposition() {
      frame = null;

      // Below the breakpoint the sidebar is a normal stacked block.
      if (window.innerWidth < DESKTOP) {
        inner.style.top = '';
        return;
      }

      var top = offset();
      var height = inner.offsetHeight;
      var room = window.innerHeight - top - BOTTOM_GAP;

      inner.style.top = height > room
        ? (window.innerHeight - height - BOTTOM_GAP) + 'px'
        : top + 'px';
    }

    function schedule() {
      if (frame) return;
      frame = window.requestAnimationFrame(reposition);
    }

    window.addEventListener('resize', schedule, { passive: true });

    // The sidebar grows as thumbnails load, so re-measure when it changes.
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(schedule).observe(inner);
    } else {
      window.addEventListener('load', schedule);
    }

    reposition();
  }

  /* Wide tables scroll inside their own box rather than the page. */
  function wrapTables(body) {
    var tables = body.querySelectorAll('table');
    for (var i = 0; i < tables.length; i++) {
      var table = tables[i];
      if (table.parentNode && table.parentNode.classList.contains('lwb-art__table-scroll')) continue;
      var wrap = document.createElement('div');
      wrap.className = 'lwb-art__table-scroll';
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    }
  }

  function setupScrollSpy(root, body) {
    var toc = root.querySelector('[data-lwb-toc]');
    if (!toc) return;

    var links = toc.querySelectorAll('a[href^="#"]');
    if (!links.length) return;

    /* Pair each TOC link with its heading, skipping any that no longer resolve. */
    var targets = [];
    for (var i = 0; i < links.length; i++) {
      var id = decodeURIComponent(links[i].getAttribute('href').slice(1));
      var heading = document.getElementById(id);
      if (heading) targets.push({ link: links[i], heading: heading });
    }
    if (!targets.length) return;

    /* Headings carry the sticky-header offset as scroll-margin-top. */
    var offset = parseInt(getComputedStyle(targets[0].heading).scrollMarginTop, 10);
    if (isNaN(offset)) offset = 100;

    var active = null;
    var ticking = false;

    function update() {
      ticking = false;

      var current = targets[0];
      for (var i = 0; i < targets.length; i++) {
        if (targets[i].heading.getBoundingClientRect().top <= offset + 8) {
          current = targets[i];
        } else {
          break;
        }
      }

      /* Once the reader reaches the bottom the last heading is the relevant one,
         even if it never scrolled past the offset. */
      if (window.innerHeight + window.pageYOffset >= document.body.scrollHeight - 4) {
        current = targets[targets.length - 1];
      }

      if (current === active) return;
      if (active) active.link.classList.remove('is-active');
      current.link.classList.add('is-active');
      active = current;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* Smooth scrolling for the sidebar links and the jump-link chips. */
  document.addEventListener('click', function (event) {
    if (!event.target || typeof event.target.closest !== 'function') return;
    var link = event.target.closest('.lwb-art__toc-list a, .lwb-art__chip');
    if (!link) return;

    var href = link.getAttribute('href') || '';
    if (href.charAt(0) !== '#' || href.length < 2) return;

    var target = document.getElementById(decodeURIComponent(href.slice(1)));
    if (!target) return;

    event.preventDefault();
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });

    /* Keep the URL shareable and move keyboard focus to the heading. */
    if (history.replaceState) history.replaceState(null, '', href);
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  function boot() {
    var roots = document.querySelectorAll('.lwb-art');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Re-run inside the theme editor when the section is re-rendered. */
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('.lwb-art');
    if (root) init(root);
  });
})();
